import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from './useAuth';

export interface Role {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RolesResponse {
  roles: Role[];
}

export interface CreateRoleRequest {
  name: string;
  description?: string;
}

export interface UpdateRoleRequest {
  name?: string;
  description?: string;
}

// Fetch all roles
export const useRoles = (search?: string) => {
  return useQuery<RolesResponse>(
    ['roles', search],
    async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      
      const response = await api.get(`/roles?${params.toString()}`);
      return response.data;
    },
    {
      staleTime: 10 * 60 * 1000, // 10 minutes - roles don't change often
    }
  );
};

// Create role mutation
export const useCreateRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation<Role, Error, CreateRoleRequest>(
    async (roleData) => {
      const response = await api.post('/roles', roleData);
      return response.data.role;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['roles']);
      },
    }
  );
};

// Update role mutation
export const useUpdateRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation<Role, Error, { id: string; data: UpdateRoleRequest }>(
    async ({ id, data }) => {
      const response = await api.put(`/roles/${id}`, data);
      return response.data.role;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['roles']);
      },
    }
  );
};

// Delete role mutation
export const useDeleteRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, string>(
    async (id) => {
      await api.delete(`/roles/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['roles']);
      },
    }
  );
};

